import { RequestOptions, CreateAxiosOptions, UploadFileParams } from "./types";
import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import AxiosCanceler from "./axiosCancel";
import { cloneDeep, isFunction } from "lodash";
import { ContentTypeEnum } from "../../../enums/httpEnum";
import env from "../../../configs/env";

export class VAxios {
  /**
   * @type {CreateAxiosOptions}
   */
  options;
  /**
   * @type {AxiosInstance}
   */
  axiosInstance;

  /**
   *
   * @param {CreateAxiosOptions} options
   */
  constructor(options) {
    this.options = options;
    this.axiosInstance = axios.create(options);
    this.setupInterceptors();
  }

  __getTransform() {
    return this.options.transform;
  }

  getAxios() {
    return this.axiosInstance;
  }

  setHeader(headers) {
    if (!this.axiosInstance) return;
    Object.assign(this.axiosInstance.defaults.headers, headers);
  }

  setupInterceptors() {
    const transform = this.__getTransform();
    if (!transform) return;

    const axiosCanceler = new AxiosCanceler();
    this.axiosInstance.interceptors.request.use((config) => {
      const { ignoreCancelToken } = config.requestOptions;
      const ignoreCancel = ignoreCancelToken
        ? ignoreCancelToken
        : (this.options.requestOptions || {}).ignoreCancelToken;
      !ignoreCancel && axiosCanceler.addPending(config);
      if (!!transform.requestInterceptors && isFunction(transform.requestInterceptors)) {
        config = transform.requestInterceptors(config, this.options);
      }
      return config;
    }, undefined);

    if (!!transform.requestInterceptorsCatch && isFunction(transform.requestInterceptorsCatch)) {
      this.axiosInstance.interceptors.request.use(undefined, transform.requestInterceptorsCatch);
    }

    this.axiosInstance.interceptors.response.use((res) => {
      !!res && axiosCanceler.removePending(res.config);
      if (transform.responseInterceptors && isFunction(transform.responseInterceptors)) {
        res = transform.responseInterceptors(res);
      }
      return res;
    }, undefined);

    if (transform.responseInterceptorsCatch && isFunction(transform.responseInterceptorsCatch)) {
      this.axiosInstance.interceptors.response.use(undefined, transform.responseInterceptorsCatch);
    }
  }

  /**
   *
   * @param {AxiosRequestConfig} config
   * @returns
   */
  __supportFormData(config) {
    const headers = config.headers || this.options.headers;
    const contentType = (headers || {})["Content-Type"] || (headers || {})["content-type"];

    if (
      contentType !== ContentTypeEnum.FORM_URLENCODED ||
      !Reflect.has(config, "data") ||
      (config.method || {}).toUpperCase() === RequestEnum.GET
    ) {
      return config;
    }

    return {
      ...config,
      data: qs.stringify(config.data, { arrayFormat: "brackets" }),
    };
  }

  /**
   * Creates form data for file upload
   * @param {UploadFileParams} params
   * @param {AxiosRequestConfig} config
   * @returns {FormData}
   */
  __createUploadFileFormData(params, config) {
    const formData = new window.FormData();
    const customFilename = params.name || "file";

    if (params.filename) {
      formData.append(customFilename, params.file, params.filename);
    } else {
      formData.append(customFilename, params.file);
    }
    config.baseURL = env.apiBaseUri;
    if (params.data) {
      Object.keys(params.data).forEach((key) => {
        const value = params.data[key];
        if (Array.isArray(value)) {
          value.forEach((item) => {
            formData.append(`${key}[]`, item);
          });
          return;
        }
        formData.append(key, params.data[key]);
      });
    }
    return formData;
  }

  /**
   * 文件上传（暴露了可取消上传的方法）
   * @param config
   * @param params
   * @param options
   * @returns {{cancel: *, uploadPromise: any}}
   */
  uploadCancellableFile(config, params, options) {
    const formData = this.__createUploadFileFormData(params, config);
    const controller = new AbortController();

    return {
      uploadPromise: this.axiosInstance
          .request({
            ...config,
            method: "POST",
            data: formData,
            headers: {
              "Content-type": ContentTypeEnum.FORM_DATA,
            },
            onUploadProgress: (progressEvent) => {
              const progressPercent = Number(((progressEvent.loaded / progressEvent.total) * 100) | 0);
              options.onUploadProgress && options.onUploadProgress(progressPercent);
            },
            signal: controller.signal
          })
          .then((res) => {
            return Promise.resolve((res || {}).data);
          }),
      cancel: () => {
        controller.abort("file upload cancelled");
      }
    }
  }

  /**
   *
   * @param {AxiosRequestConfig} config
   * @param {UploadFileParams} params
   * @returns
   */
  uploadFile(config, params, options) {
    const formData = this.__createUploadFileFormData(params, config);

    return this.axiosInstance
        .request({
          ...config,
          method: "POST",
          data: formData,
          headers: {
            "Content-type": ContentTypeEnum.FORM_DATA,
            ignoreCancelToken: true,
          },
          onUploadProgress: (progressEvent) => {
            const progressPercent = Number(((progressEvent.loaded / progressEvent.total) * 100) | 0);
            options.onUploadProgress && options.onUploadProgress(progressPercent);
          },
        })
        .then((res) => {
          return Promise.resolve((res || {}).data);
        });
  }

  /**
   *
   * @param {AxiosRequestConfig} config
   * @param {RequestOptions} options
   * @returns {Promise<any>}
   */
  get(config, options) {
    return this.request({ ...config, method: "GET" }, options);
  }

  /**
   * 可取消的 get 方法；
   * 注意：最终的取消动作由业务层决定
   * @param config
   * @param options
   * @returns {{cancel: (function(): void), promise: Promise<*>}}
   */
  getWithCancel(config, options) {
    const controller = new AbortController();
    const promise = this.get({
      ...config,
      signal: controller.signal
    }, options);
    return {
      promise,
      cancel: () => controller.abort()
    };
  }

  /**
   *
   * @param {AxiosRequestConfig} config
   * @param {RequestOptions} options
   * @returns {Promise<any>}
   */
  post(config, options) {
    return this.request({ ...config, method: "POST" }, options);
  }

  /**
   *　可取消的 post 方法；
   * 注意：最终的取消动作由业务层决定
   * @param config
   * @param options
   * @returns {{cancel: (function(): void), promise: Promise<*>}}
   */
  postWithCancel(config, options) {
    const controller = new AbortController();
    const promise = this.post({
      ...config,
      signal: controller.signal
    }, options);
    return {
      promise,
      cancel: () => controller.abort()
    };
  }

  /**
   *
   * @param {AxiosRequestConfig} config
   * @param {RequestOptions} options
   * @returns {Promise<any>}
   */
  put(config, options) {
    return this.request({ ...config, method: "PUT" }, options);
  }

  /**
   *
   * @param {AxiosRequestConfig} config
   * @param {RequestOptions} options
   * @returns {Promise<any>}
   */
  delete(config, options) {
    return this.request({ ...config, method: "DELETE" }, options);
  }

  /**
   *
   * @param {AxiosRequestConfig} config
   * @param {RequestOptions} options
   * @returns
   */
  request(config, options) {
    let conf = cloneDeep(config);
    const transform = this.__getTransform();

    const { requestOptions } = this.options;

    const opt = Object.assign({}, requestOptions, options);

    const { beforeRequestHook, requestCatchHook, transformRequestHook } = transform || {};
    if (!!beforeRequestHook && isFunction(beforeRequestHook)) {
      conf = beforeRequestHook(conf, opt);
    }
    conf.requestOptions = opt;

    conf = this.__supportFormData(conf);

    return new Promise((resolve, reject) => {
      this.axiosInstance
        .request(conf)
        .then((res) => {
          if (transformRequestHook && isFunction(transformRequestHook)) {
            try {
              const ret = transformRequestHook(res, opt);
              config.success && config.success(res.data);
              resolve(ret);
            } catch (err) {
              reject(err || new Error("request error!"));
            }
            return;
          }
          resolve(res);
        })
        .catch((e) => {
          if (requestCatchHook && isFunction(requestCatchHook)) {
            reject(requestCatchHook(e, opt));
            return;
          }
          if (axios.isAxiosError(e)) {
            // 在此处重写来自axios的错误消息
          }
          reject(e);
        });
    });
  }
}
